import { Link } from '@tanstack/react-router';
import { ArrowRight, Heart, MapPin, Sparkles, Users } from 'lucide-react';
import { MarketingShell } from './MarketingShell';

const VALUES = [
  { icon: Heart, title: 'Local first', body: 'We build for Bangladeshi shops — the currency, the languages, the way trade actually happens here.' },
  { icon: Sparkles, title: 'Calm software', body: 'Restraint over clutter. Software that helps you close up faster, not one more thing to manage.' },
  { icon: Users, title: 'Real support', body: 'A team you can call, in Sylhet, who understands your business — not a ticket queue abroad.' },
];

export function About() {
  return (
    <MarketingShell>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-10">
        <p className="text-xs font-semibold uppercase tracking-wider text-brand">About</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-[1.05]">
          Software for the shops we grew up around.
        </h1>
        <div className="mt-6 space-y-4 text-lg text-zinc-600 dark:text-zinc-300 leading-relaxed">
          <p>
            Possignin is built by <span className="font-medium text-zinc-900 dark:text-zinc-100">ITCity</span>, a
            software team in Sylhet, Bangladesh. We started Possignin because the shops around us — bakeries,
            pharmacies, electronics stores, repair workshops — were running on notebooks, calculators, and memory.
          </p>
          <p>
            The tools that existed were either too expensive, too complicated, or built for somewhere else. So we
            made one that fits here: priced in taka, fluent in Bangla, and patient enough for a busy counter.
          </p>
          <p>
            Today Possignin runs over a hundred businesses — directly and through resellers who sell it under their
            own brand — and it grows the way our customers tell us to.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid sm:grid-cols-3 gap-4">
        {VALUES.map((v) => (
          <div key={v.title} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 p-6">
            <span className="w-10 h-10 grid place-items-center rounded-lg bg-brand-subtle dark:bg-brand/15 text-brand dark:text-brand-dm">
              <v.icon className="w-5 h-5" />
            </span>
            <h2 className="mt-4 font-semibold text-zinc-950 dark:text-zinc-50">{v.title}</h2>
            <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{v.body}</p>
          </div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/70 dark:bg-zinc-900/40 p-8 sm:p-10">
          <div className="flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
            <MapPin className="w-4 h-4 text-brand dark:text-brand-dm" /> Sylhet, Bangladesh
          </div>
          <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 max-w-xl">
            Want to build your business on Possignin?
          </h2>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to="/signup" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-brand hover:bg-brand/90 transition">
              Start free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/become-a-reseller" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border border-zinc-300 dark:border-zinc-700 hover:bg-white dark:hover:bg-zinc-800 transition">
              Become a reseller
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
